<template>
  <asc-dialog
    :width="1100"
    :text="name"
    :show-dialog="showDialog"
    primary-button-text="Update"
    :on-primary="onUpdateClicked"
    :primary-available="updateEnabled"
    :on-close="onClose"
    :loading="loadingButton"
    class="analyticDialog"
  >
    <template v-if="showDialog">
      <div class="analyticDialog-content">
        <asc-spinner-wrapper :loading="loadingWrapper">
          <asc-analytic-dialog-header
            :analytic-details="analyticDetails"
            :components="components"
            :airlines="airlines"
            @update-header="updateHeader"
          ></asc-analytic-dialog-header>
          <v-row class="mb-4" no-gutters>
            <v-col cols="12">
              <asc-accordion :value="1">
                <asc-accordion-item>
                  <template slot="header"> Visualizations </template>
                  <template v-if="features.length">
                    <asc-analytic-visualization-table
                      class="visualizationTable"
                      :features="allFeatures"
                      @update-feature="updateFeatureDetails"
                    ></asc-analytic-visualization-table>
                  </template>
                  <template v-else>
                    <div>
                      <asc-label
                        size="sm"
                        :value="getVisualizationLabelText"
                      ></asc-label>
                    </div>
                  </template>
                </asc-accordion-item>
              </asc-accordion>
            </v-col>
          </v-row>
          <v-row class="mb-4" no-gutters>
            <v-col cols="12">
              <asc-accordion
                :value="1"
                class="alertsTable--accordion"
                :style="{ 'z-index': editorDialogOpen ? 0 : 2 }"
              >
                <asc-accordion-item>
                  <template slot="header"> Alerts </template>
                  <template v-if="alertTypes.length">
                    <asc-analytic-alerts-table
                      :analytic-details="analyticDetails"
                      :components="components"
                      :airlines="airlines"
                      :alert-types="allAlerts"
                      :issue-types="issueTypes"
                      class="alertsTable"
                      @update-alert="updateAlerts"
                    ></asc-analytic-alerts-table>
                  </template>
                  <template v-else>
                    <div>
                      <asc-label
                        size="sm"
                        :value="getAlertsLabelText"
                      ></asc-label>
                    </div>
                  </template>
                </asc-accordion-item>
              </asc-accordion>
            </v-col>
          </v-row>
          <v-row class="mb-4" no-gutters>
            <v-col cols="12">
              <asc-accordion :value="1" class="associatedAlerts--accordion">
                <asc-accordion-item>
                  <template slot="header"> Associated Analytics </template>
                  <asc-associated-analytics-grid
                    :values="associatedAnalytics"
                  ></asc-associated-analytics-grid>
                  <div
                    v-if="!associatedAnalytics.length"
                    class="asc-analytic-dialog--container-label"
                  >
                    <asc-label
                      class="asc-analytic-dialog--container-label-text"
                      size="sm"
                      :value="getLabelText"
                    ></asc-label>
                  </div>
                </asc-accordion-item>
              </asc-accordion>
            </v-col>
          </v-row>
          <v-row class="mb-4" no-gutters>
            <v-col cols="12">
              <asc-accordion
                :value="1"
                class="maintenanceRecommendation--accordion"
              >
                <asc-accordion-item>
                  <template slot="header">
                    Maintenance Recommendation
                  </template>

                  <asc-required-label
                    label="Maintenance Recommendation Title"
                    :required="true"
                  >
                  </asc-required-label>
                  <asc-text-field-input
                    v-model="mxRecTitle"
                    required
                    :counter="true"
                    :max-length="45"
                    :rules="validationRules.mxRecTitle"
                  >
                  </asc-text-field-input>

                  <asc-required-label
                    label="Maintenance Recommendation Details"
                    :required="true"
                  >
                  </asc-required-label>

                  <asc-rich-text-input
                    class="richTextInput--dialogue"
                    required
                    :comment="getRecommendationText"
                    :can-edit="true"
                    :max-length="3500"
                    :counter="true"
                    hidden-menu
                    @updated="handleUpdatedText"
                    @editorDialogOpen="handleEditorDialogOpen"
                  >
                  </asc-rich-text-input>
                  <asc-required-label label="ATA Chapter" :required="true">
                  </asc-required-label>
                  <asc-autocomplete-selector
                    id="ataChapter"
                    v-model="ataChapter"
                    class="ataSelector"
                    use-array
                    component-type="single"
                    :items="ataChapterList"
                    :on-change="updateAtaChapter"
                    :rules="validationRules.ataChapter"
                  ></asc-autocomplete-selector>
                  <template v-if="!hasData">
                    <asc-validation-message
                      class="validation"
                      message="This field is required for submission"
                      :show="!hasData"
                    ></asc-validation-message>
                  </template>
                </asc-accordion-item>
              </asc-accordion>
            </v-col>
          </v-row>
        </asc-spinner-wrapper>
      </div>
    </template>
  </asc-dialog>
</template>

<script>
import AscDialog from '@/components/atoms/AscDialog'
import AscSpinnerWrapper from '@/components/atoms/AscSpinnerWrapper'
import AscAccordion from '@/components/atoms/AscAccordion'
import AscAutocompleteSelector from '@/components/molecules/AscAutocompleteSelector'
import AscAccordionItem from '@/components/atoms/AscAccordionItem'
import AscAnalyticDialogHeader from './AscAnalyticDialogHeader'
import AscAnalyticVisualizationTable from './AscAnalyticVisualizationTable.vue'
import AscAnalyticAlertsTable from './AscAnalyticAlertsTable.vue'
import AscAssociatedAnalyticsGrid from './AscAssociatedAnalyticsGrid.vue'
import AscLabel from '@/components/atoms/AscLabel'
import _ from 'lodash'
import AscTextFieldInput from '../atoms/AscTextFieldInput.vue'
import AscRichTextInput from '@/components/atoms/AscRichTextInput'
import AscValidationMessage from '@/components/atoms/AscValidationMessage'
import AscRequiredLabel from '@/components/atoms/AscRequiredLabel'

export default {
  name: 'AscAnalyticDialog',

  components: {
    AscDialog,
    AscSpinnerWrapper,
    AscAutocompleteSelector,
    AscAccordion,
    AscAccordionItem,
    AscAnalyticDialogHeader,
    AscAnalyticVisualizationTable,
    AscAnalyticAlertsTable,
    AscAssociatedAnalyticsGrid,
    AscLabel,
    AscTextFieldInput,
    AscRichTextInput,
    AscValidationMessage,
    AscRequiredLabel,
  },

  props: {
    onPrimary: {
      type: Function,
      default: () => {},
    },
    primaryAvailable: {
      type: Boolean,
      default: true,
    },
    onClose: {
      type: Function,
      default: () => {},
    },
    components: {
      type: Array,
      default: () => [],
    },
    airlines: {
      type: Array,
      default: () => [],
    },
    showDialog: {
      type: Boolean,
      default: false,
    },
    loadingWrapper: {
      type: Boolean,
      default: false,
    },
    loadingButton: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: '',
    },
    analyticDetails: {
      type: Object,
      default: () => {},
    },
    features: {
      type: Array,
      default: () => [],
    },
    alertTypes: {
      type: Array,
      default: () => [],
    },
    allAnalytics: {
      type: Array,
      default: () => [],
    },
    issueTypes: {
      type: Array,
      default: () => [],
    },
    ataChapterList: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      newAnalyticDetails: { ...this.analyticDetails },
      updatedFeatures: [],
      updatedAlerts: [],
      allFeatures: [],
      allAlerts: [],
      editorDialogOpen: false,
      validationRules: {
        mxRecTitle: [v => (!!v && !!v.trim()) || 'Mx Rec Title is required'],
        ataChapter: [a => (!!a && !!a.trim()) || 'ATA Chapter is required'],
      },
    }
  },

  computed: {
    getLabelText() {
      return `There are no associated analytics for module ${this.name}`
    },
    getAlertsLabelText() {
      return `There are no alerts for analytic module ${this.name}`
    },
    getVisualizationLabelText() {
      return `There are no visualizations for analytic module ${this.name}`
    },
    associatedAnalytics() {
      return this.allAnalytics?.length && this.newAnalyticDetails?.id
        ? this.allAnalytics.filter(
            item => item.analyticModuleId === this.newAnalyticDetails.id,
          )
        : []
    },
    hasData() {
      return this.newAnalyticDetails.mxRecDetails === '<p></p>' ? false : true
    },
    getRecommendationText() {
      return this.analyticDetails?.mxRecDetails
    },
    mxRecTitle: {
      get() {
        return this.analyticDetails?.mxRecTitle
      },
      set(val) {
        this.newAnalyticDetails.mxRecTitle = val
      },
    },
    ataChapter: {
      get() {
        return parseInt(this.analyticDetails?.ataChapter)
      },
      set(val) {
        if (val.length !== 0 || this.ataChapter.length !== 0) {
          this.$emit('updateAtaChapter', val)
        }
      },
    },
    updateEnabled() {
      return this.newAnalyticDetails.mxRecDetails !== '<p></p>' &&
        this.newAnalyticDetails.mxRecDetails !== null &&
        this.newAnalyticDetails.mxRecTitle &&
        this.newAnalyticDetails.ataChapter
        ? this.primaryAvailable
        : false
    },
  },

  watch: {
    analyticDetails(newValue) {
      this.populateNewAnalyticDetails(newValue)
    },
    features(newValue) {
      this.allFeatures = newValue
    },
    alertTypes(newValue) {
      this.allAlerts = newValue
    },
  },

  methods: {
    onUpdateClicked() {
      this.$emit(
        'updateClick',
        _.omit(
          {
            details: this.newAnalyticDetails,
            features: this.updatedFeatures,
            alerts: this.updatedAlerts,
          },
          ['details.alertTypes', 'details.features'],
        ),
      )
    },
    handleUpdatedText(text) {
      this.newAnalyticDetails.mxRecDetails = text
    },
    handleEditorDialogOpen(isOpen) {
      this.editorDialogOpen = isOpen
    },
    updateHeader(item) {
      this.newAnalyticDetails[item.key] = item.value
    },
    updateFeatureDetails({ feature, index }) {
      const isUpdated = this.updatedFeatures.findIndex(
        item => item.id === feature.id,
      )
      this.allFeatures[index] = feature
      if (isUpdated !== -1) {
        this.updatedFeatures[isUpdated] = feature
      } else {
        this.updatedFeatures.push(feature)
      }
    },
    updateAlerts({ alert, index }) {
      const isUpdated = this.updatedAlerts.findIndex(
        item => item.id === alert.id,
      )
      this.allAlerts[index] = alert
      if (isUpdated !== -1) {
        this.updatedAlerts[isUpdated] = alert
      } else {
        this.updatedAlerts.push(alert)
      }
    },
    populateNewAnalyticDetails(newValue) {
      if (newValue !== null && newValue !== {}) {
        this.newAnalyticDetails = { ...newValue }
      } else {
        this.newAnalyticDetails = {}
      }
    },
    updateAtaChapter(id, selection, selectionObject) {
      if (selectionObject.length) {
        this.newAnalyticDetails.ataChapterId = selectionObject[0].id
        this.newAnalyticDetails.ataChapter = selectionObject[0].ataChapter
      } else {
        this.newAnalyticDetails.ataChapterId = null
        this.newAnalyticDetails.ataChapter = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.asc-analytic-dialog--container {
  &-label {
    width: calc(100% - 32px);
    background-color: #f7f7f7;
    padding: 10px;
    position: absolute;
  }
}

.analyticDialog-content {
  margin: 10px;
}

.alertsTable--accordion {
  position: relative;
}

.associatedAlerts--accordion {
  & > .asc-accordion > .v-expansion-panel--active {
    min-height: 170px;
  }
}
</style>
